import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { Box, Typography } from '@mui/material'
import React from 'react'

import { ActionsContainer } from '@/app/component/form/common'
import { LocalAction, LocalActionType, LocalNonEmptyAction } from '@/app/module/campaigns/types'
import { getDisabledIndexesFrom } from '@/app/module/campaigns/utils/actions/disable-rules'
import { ActionOption } from '@/app/module/campaigns/utils/actions/options'
import { FilesType, PersonalizationType } from '@/app/types'

import { getActionErrors } from '../../utils/actions/validation'
import ActionListItem from './action-list-item'
import AddActionButton from './add-action-button'

type Props = {
  actionOptions: ActionOption[]
  actions: LocalAction[]
  files: FilesType
  personalizationList: PersonalizationType

  onActionAddToLibrary: (action: LocalNonEmptyAction) => Promise<void>
  onActionChange: (value: LocalActionType, index: number) => void
  onActionMove: (from: number, to: number) => void
  onActionRemove: (index: number) => void
}

const ActionList: React.FC<Props> = ({
  actionOptions,
  actions,
  files,
  personalizationList,
  onActionAddToLibrary,
  onActionChange,
  onActionMove,
  onActionRemove,
}) => {
  const disabledIndexesFrom: Record<string, number> = React.useMemo(() => getDisabledIndexesFrom(actions), [actions])
  const actionErrors = React.useMemo(() => getActionErrors(actions, actionOptions), [actions, actionOptions])
  const normalActionOptions = React.useMemo(() => actionOptions.filter((opt) => !opt.final), [actionOptions])

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return
    }

    onActionMove(result.source.index, result.destination.index)
  }

  const handleAddAction = (value: LocalActionType | 'addFromLibrary') => {
    onActionChange(value as LocalActionType, actions.length)
  }

  const targetIndex = actions.length

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box display="flex" mt={2.5}>
        <Box width={140} pt={1}>
          <Typography>List of Actions</Typography>
        </Box>
        <Box width="100%">
          <Droppable droppableId="campaign-actions-list">
            {(provided) => (
              <div className="campaign-actions-list" ref={provided.innerRef} {...provided.droppableProps}>
                {actions.map((action, index) => {
                  const draggableId = (action as any).id || `action-${index}`
                  const isDragDisabled = actions.length <= 1
                  const error = actionErrors[index]

                  return (
                    <Draggable
                      key={draggableId}
                      draggableId={draggableId}
                      index={index}
                      isDragDisabled={isDragDisabled || !!error?.disabled}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <ActionListItem
                          action={action}
                          actionOptions={actionOptions}
                          disabled={error?.disabled}
                          dragProvided={dragProvided}
                          dragSnapshot={dragSnapshot}
                          errorMessage={error?.message}
                          files={files}
                          index={index}
                          isLast={false}
                          personalizationList={personalizationList}
                          onAddToLibrary={onActionAddToLibrary}
                          onChange={onActionChange}
                          onMove={onActionMove}
                          onRemove={onActionRemove}
                        />
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {!actions.length && (
            <AddActionButton
              actionOptions={normalActionOptions}
              disabledIndexesFrom={disabledIndexesFrom}
              targetIndex={targetIndex}
              onAddAction={handleAddAction}
            />
          )}
          {!!actions.length && (
            <ActionsContainer sx={{ justifyContent: 'flex-start', paddingTop: 0 }}>
              <AddActionButton
                actionOptions={normalActionOptions}
                disabledIndexesFrom={disabledIndexesFrom}
                targetIndex={targetIndex}
                onAddAction={handleAddAction}
              />
            </ActionsContainer>
          )}
        </Box>
      </Box>
    </DragDropContext>
  )
}

export default ActionList
